
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, MessageCircle, Share2 } from "lucide-react";
import { HealthJournal } from "@/types/database.types";
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Separator } from "@/components/ui/separator";

interface JournalEntriesProps {
  entries: HealthJournal[] | null;
  isLoading: boolean;
}

const getMoodDisplay = (mood: string) => {
  const moodMap: Record<string, { label: string, className: string, emoji: string }> = {
    'EXCELLENT': { 
      label: 'Excellent', 
      className: 'bg-green-100 text-green-800 border-green-200', 
      emoji: '😃' 
    },
    'GOOD': { 
      label: 'Bon', 
      className: 'bg-blue-100 text-blue-800 border-blue-200', 
      emoji: '🙂' 
    },
    'NEUTRAL': { 
      label: 'Moyen', 
      className: 'bg-yellow-100 text-yellow-800 border-yellow-200', 
      emoji: '😐' 
    },
    'TIRED': { 
      label: 'Fatigué', 
      className: 'bg-orange-100 text-orange-800 border-orange-200', 
      emoji: '😴' 
    },
    'STRESSED': { 
      label: 'Stressé', 
      className: 'bg-red-100 text-red-800 border-red-200', 
      emoji: '😰' 
    },
    'ANXIOUS': { 
      label: 'Anxieux', 
      className: 'bg-purple-100 text-purple-800 border-purple-200', 
      emoji: '😟' 
    },
  };

  return moodMap[mood] || { 
    label: mood, 
    className: 'bg-gray-100 text-gray-800 border-gray-200', 
    emoji: '❓' 
  };
};

export const JournalEntries = ({ entries, isLoading }: JournalEntriesProps) => {
  if (isLoading) {
    return (
      <div className="flex justify-center animate-fade-in">
        <div className="animate-pulse space-y-6 w-full">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="w-full border shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex justify-between">
                  <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-6 bg-gray-200 rounded w-24"></div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                  <div className="h-10 bg-gray-200 rounded w-full"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Journal Entries Section */}
      <div className="grid gap-5">
        {entries?.map((entry) => {
          const moodInfo = getMoodDisplay(entry.mood);
          const entryDate = new Date(entry.date);
          
          return (
            <Card 
              key={entry.id}
              className="overflow-hidden hover:shadow-md transition-all duration-300 group bg-card border-muted/80"
            >
              <div className={`h-1 w-full ${moodInfo.className.replace('bg-', 'bg-')}`} />
              <CardHeader className="pb-2">
                <div className="flex flex-wrap justify-between items-center">
                  <div className="space-y-1">
                    <CardTitle className="text-lg font-medium">
                      {entryDate.toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(entryDate, { addSuffix: true, locale: fr })}
                    </p>
                  </div>
                  
                  <div className={`px-3 py-1 rounded-full text-sm border ${moodInfo.className} flex items-center gap-1.5 transition-colors`}>
                    <span>{moodInfo.emoji}</span>
                    <span>{moodInfo.label}</span>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-4">
                  {entry.symptoms && entry.symptoms.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 text-muted-foreground">Symptômes:</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {entry.symptoms.map((symptom, index) => (
                          <span 
                            key={index}
                            className="bg-muted/50 hover:bg-muted text-muted-foreground px-2 py-1 rounded-md text-xs transition-colors"
                          >
                            {symptom}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {entry.notes && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 text-muted-foreground">Notes:</h4>
                      <p className="text-sm whitespace-pre-wrap">{entry.notes}</p>
                    </div>
                  )}
                  
                  <Separator className="my-3" />
                  
                  <div className="flex justify-between items-center">
                    <div className="flex gap-3">
                      <Button variant="ghost" size="sm" className="h-8 px-2 text-muted-foreground hover:text-foreground">
                        <Heart className="h-4 w-4 mr-1" /> <span className="text-xs">12</span>
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 px-2 text-muted-foreground hover:text-foreground">
                        <MessageCircle className="h-4 w-4 mr-1" /> <span className="text-xs">5</span>
                      </Button>
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-muted-foreground hover:text-foreground">
                      <Share2 className="h-4 w-4 mr-1" /> <span className="text-xs">Partager</span>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
